<script lang="ts">
	import { _ } from 'svelte-i18n';
	import { fade, fly, scale } from 'svelte/transition';
	import { onMount } from 'svelte';
	import Header from '$lib/components/sections/Header.svelte';
	import Footer from '$lib/components/sections/Footer.svelte';
	import SeoContent from '$lib/components/sections/SeoContent.svelte';
	import Button from '$lib/components/primitives/Button.svelte';
	import Icon from '$lib/components/primitives/Icon.svelte';

	let visible = $state(true); // SSR: start visible so hero + <h1> are in server HTML (Ahrefs: "H1 tag missing"). onMount re-set is a no-op; entrance animations still play on client-side navigation.
	let currentMetric = $state(0);

	onMount(() => {
		visible = true;
		// Animate metrics
		const interval = setInterval(() => {
			currentMetric = (currentMetric + 1) % financialMetrics.length;
		}, 3000);
		return () => clearInterval(interval);
	});

	const spreadsheetHorrors = [
		{
			scenario: $_('features.finance.horrors.horror1.scenario'),
			pain: $_('features.finance.horrors.horror1.pain'),
			solution: $_('features.finance.horrors.horror1.solution')
		},
		{
			scenario: $_('features.finance.horrors.horror2.scenario'),
			pain: $_('features.finance.horrors.horror2.pain'),
			solution: $_('features.finance.horrors.horror2.solution')
		},
		{
			scenario: $_('features.finance.horrors.horror3.scenario'),
			pain: $_('features.finance.horrors.horror3.pain'),
			solution: $_('features.finance.horrors.horror3.solution')
		}
	];

	const financialMetrics = [
		{
			label: $_('features.finance.metrics.metric1.label'),
			value: $_('features.finance.metrics.metric1.value'),
			change: $_('features.finance.metrics.metric1.change'),
			trend: 'up'
		},
		{
			label: $_('features.finance.metrics.metric2.label'),
			value: $_('features.finance.metrics.metric2.value'),
			change: $_('features.finance.metrics.metric2.change'),
			trend: 'down'
		},
		{
			label: $_('features.finance.metrics.metric3.label'),
			value: $_('features.finance.metrics.metric3.value'),
			change: $_('features.finance.metrics.metric3.change'),
			trend: 'up'
		},
		{
			label: $_('features.finance.metrics.metric4.label'),
			value: $_('features.finance.metrics.metric4.value'),
			change: $_('features.finance.metrics.metric4.change'),
			trend: 'stable'
		}
	];

	const solutions = [
		{
			icon: 'trending-up',
			title: $_('features.finance.solutions.solution1.title'),
			description: $_('features.finance.solutions.solution1.description'),
			benefit: $_('features.finance.solutions.solution1.benefit')
		},
		{
			icon: 'link',
			title: $_('features.finance.solutions.solution2.title'),
			description: $_('features.finance.solutions.solution2.description'),
			benefit: $_('features.finance.solutions.solution2.benefit')
		},
		{
			icon: 'bar-chart',
			title: $_('features.finance.solutions.solution3.title'),
			description: $_('features.finance.solutions.solution3.description'),
			benefit: $_('features.finance.solutions.solution3.benefit')
		},
		{
			icon: 'shield',
			title: $_('features.finance.solutions.solution4.title'),
			description: $_('features.finance.solutions.solution4.description'),
			benefit: $_('features.finance.solutions.solution4.benefit')
		}
	];
</script>

<svelte:head>
	<title>{$_('features.finance.meta.title')}</title>
	<meta name="description" content={$_('features.finance.meta.description')} />
	<meta property="og:title" content="{$_('features.finance.meta.title')}" />
	<meta property="og:description" content={$_('features.finance.meta.description')} />
</svelte:head>

<Header />

<div class="feature-page">
	<!-- Hero Section -->
	<section class="hero">
		<div class="container">
			{#if visible}
				<div class="hero__grid">
					<div class="hero__content" in:fade={{ duration: 600, delay: 100 }}>
						<div class="hero__pain" in:fly={{ y: 30, duration: 600, delay: 200 }}>
							<p class="hero__confession">{$_('features.finance.hero.confession')}</p>
						</div>
						<h1 class="hero__title" in:fly={{ y: 30, duration: 600, delay: 300 }}>
							{@html $_('features.finance.hero.title')}
						</h1>
						<p class="hero__subtitle" in:fly={{ y: 30, duration: 600, delay: 400 }}>
							{$_('features.finance.hero.subtitle')}
						</p>
						<div class="hero__actions" in:fly={{ y: 30, duration: 600, delay: 500 }}>
							<Button variant="primary" size="lg" href="https://app.heyweek.com/?signup">
								{$_('features.finance.hero.primaryCta')}
							</Button>
							<Button variant="ghost" size="lg" href="#horrors">
								{$_('features.finance.hero.secondaryCta')}
							</Button>
						</div>
					</div>
					<div class="hero__visual" in:scale={{ duration: 800, delay: 400 }}>
						<div class="spreadsheet-nightmare">
							<div class="spreadsheet-header">
								<div class="spreadsheet-tab active">
									{$_('features.finance.widget.spreadsheetName')}
								</div>
								<div class="spreadsheet-error">❌</div>
							</div>
							<div class="spreadsheet-grid">
								<div class="spreadsheet-cell error">##ERROR##</div>
								<div class="spreadsheet-cell">{$_('features.finance.widget.coffee')}</div>
								<div class="spreadsheet-cell missing">
									{$_('features.finance.widget.receiptMissing')}
								</div>
								<div class="spreadsheet-cell error">#DIV/0!</div>
								<div class="spreadsheet-cell">{$_('features.finance.widget.hotel')}</div>
								<div class="spreadsheet-cell missing">
									{$_('features.finance.widget.categoryMissing')}
								</div>
							</div>
							<div class="spreadsheet-arrow">→</div>
							<div class="solution-preview">
								<div class="solution-header">{$_('features.finance.widget.solutionHeader')}</div>
								<div class="metrics-card">
									{#each financialMetrics as metric, index}
										<div class="metric" class:active={index === currentMetric}>
											<div class="metric__value">{metric.value}</div>
											<div class="metric__label">{metric.label}</div>
										</div>
									{/each}
								</div>
							</div>
						</div>
					</div>
				</div>
			{/if}
		</div>
	</section>

	<!-- Spreadsheet Horrors -->
	<section class="horrors" id="horrors">
		<div class="container">
			<div class="horrors__header">
				<h2 class="horrors__title">{$_('features.finance.horrors.title')}</h2>
				<p class="horrors__subtitle">{$_('features.finance.horrors.subtitle')}</p>
			</div>
			<div class="horrors__list">
				{#each spreadsheetHorrors as horror, index}
					<div class="horror-story" style="animation-delay: {index * 300}ms">
						<div class="horror-story__scenario">
							<h3 class="horror-story__title">{horror.scenario}</h3>
							<div class="horror-story__pain">
								<Icon name="frown" size={20} style="color: #ef4444; flex-shrink: 0;" />
								<p>{horror.pain}</p>
							</div>
						</div>
						<div class="horror-story__solution">
							<div class="horror-story__fix">
								<Icon
									name="check-circle"
									size={20}
									style="color: rgb(255, 94, 126); flex-shrink: 0;"
								/>
								<p>{horror.solution}</p>
							</div>
						</div>
					</div>
				{/each}
			</div>
		</div>
	</section>

	<!-- Solutions Section -->
	<section class="solutions">
		<div class="container">
			<div class="solutions__header">
				<h2 class="solutions__title">{$_('features.finance.solutions.title')}</h2>
				<p class="solutions__subtitle">{$_('features.finance.solutions.subtitle')}</p>
			</div>
			<div class="solutions__grid">
				{#each solutions as solution}
					<div class="solution-card">
						<div class="solution-card__icon">
							<Icon name={solution.icon} size={32} style="color: rgb(255, 94, 126);" />
						</div>
						<div class="solution-card__content">
							<h3 class="solution-card__title">{solution.title}</h3>
							<p class="solution-card__description">{solution.description}</p>
							<div class="solution-card__benefit">
								<Icon name="check" size={16} style="color: #10b981;" />
								<span>{solution.benefit}</span>
							</div>
						</div>
					</div>
				{/each}
			</div>
		</div>
	</section>

	<!-- Before/After Section -->
	<section class="before-after">
		<div class="container">
			<div class="before-after__grid">
				<div class="before-after__side before">
					<h3 class="before-after__title">
						<Icon name="x" size={24} style="color: #ef4444;" />
						{$_('features.finance.beforeAfter.before.title')}
					</h3>
					<ul class="before-after__list">
						<li>{$_('features.finance.beforeAfter.before.point1')}</li>
						<li>{$_('features.finance.beforeAfter.before.point2')}</li>
						<li>{$_('features.finance.beforeAfter.before.point3')}</li>
						<li>{$_('features.finance.beforeAfter.before.point4')}</li>
						<li>{$_('features.finance.beforeAfter.before.point5')}</li>
					</ul>
				</div>
				<div class="before-after__side after">
					<h3 class="before-after__title">
						<Icon name="check" size={24} style="color: rgb(255, 94, 126);" />
						{$_('features.finance.beforeAfter.after.title')}
					</h3>
					<ul class="before-after__list">
						<li>{$_('features.finance.beforeAfter.after.point1')}</li>
						<li>{$_('features.finance.beforeAfter.after.point2')}</li>
						<li>{$_('features.finance.beforeAfter.after.point3')}</li>
						<li>{$_('features.finance.beforeAfter.after.point4')}</li>
						<li>{$_('features.finance.beforeAfter.after.point5')}</li>
					</ul>
				</div>
			</div>
		</div>
	</section>

	<!-- CTA Section -->
	<section class="cta">
		<div class="container">
			<div class="cta__content">
				<h2 class="cta__title">{$_('features.finance.cta.title')}</h2>
				<p class="cta__subtitle">
					{$_('features.finance.cta.subtitle')}
				</p>
				<div class="cta__actions">
					<Button variant="primary" size="lg" href="https://app.heyweek.com/?signup">
						{$_('features.finance.cta.primaryButton')}
					</Button>
					<Button variant="ghost" size="lg" href="/pricing">
						{$_('features.finance.cta.secondaryButton')}
					</Button>
				</div>
			</div>
		</div>
	</section>
</div>

<SeoContent slug="finance" />

<Footer />

<style>
	.feature-page {
		min-height: 100vh;
	}

	/* Hero Section - Emotional layout */
	.hero {
		padding: var(--space-20) 0;
		background: linear-gradient(
			135deg,
			rgba(239, 68, 68, 0.05) 0%,
			transparent 50%,
			rgba(255, 94, 126, 0.05) 100%
		);
	}

	.hero__grid {
		display: grid;
		grid-template-columns: 1fr 1fr;
		gap: var(--space-16);
		align-items: center;
		max-width: 1200px;
		margin: 0 auto;
	}

	.hero__pain {
		margin-bottom: var(--space-6);
	}

	.hero__confession {
		font-size: var(--font-size-lg);
		color: var(--text-secondary);
		font-style: italic;
		margin: 0;
	}

	.hero__title {
		font-size: var(--font-size-5xl);
		font-weight: var(--font-weight-bold);
		line-height: 1.1;
		color: var(--text-primary);
		margin-bottom: var(--space-6);
		letter-spacing: -0.02em;
	}

	.hero__subtitle {
		font-size: var(--font-size-lg);
		color: var(--text-secondary);
		line-height: var(--line-height-relaxed);
		margin-bottom: var(--space-8);
	}

	.hero__actions {
		display: flex;
		gap: var(--space-4);
		flex-wrap: wrap;
	}

	/* Spreadsheet Nightmare Visual */
	.spreadsheet-nightmare {
		position: relative;
		max-width: 500px;
		margin: 0 auto;
	}

	.spreadsheet-header {
		display: flex;
		align-items: center;
		justify-content: space-between;
		padding: var(--space-3) var(--space-4);
		background: var(--bg-secondary);
		border: 1px solid var(--border-light);
		border-bottom: none;
		border-radius: var(--radius-md) var(--radius-md) 0 0;
	}

	.spreadsheet-tab {
		font-size: var(--font-size-sm);
		color: var(--text-secondary);
		font-family: 'Courier New', monospace;
	}

	.spreadsheet-error {
		font-size: var(--font-size-lg);
	}

	.spreadsheet-grid {
		display: grid;
		grid-template-columns: 1fr 1fr;
		gap: 1px;
		background: var(--border-light);
		border: 1px solid var(--border-light);
		border-top: none;
		margin-bottom: var(--space-6);
	}

	.spreadsheet-cell {
		padding: var(--space-2);
		background: var(--bg-primary);
		font-size: var(--font-size-sm);
		font-family: 'Courier New', monospace;
		color: var(--text-primary);
	}

	.spreadsheet-cell.error {
		color: #ef4444;
		background: var(--bg-secondary);
		opacity: 0.9;
	}

	.spreadsheet-cell.missing {
		color: #f59e0b;
		background: var(--bg-secondary);
		font-style: italic;
		opacity: 0.9;
	}

	.spreadsheet-arrow {
		font-size: var(--font-size-2xl);
		color: rgb(255, 94, 126);
		text-align: center;
		margin-bottom: var(--space-6);
		animation: bounce 2s infinite;
	}

	@keyframes bounce {
		0%,
		20%,
		50%,
		80%,
		100% {
			transform: translateX(0);
		}
		40% {
			transform: translateX(10px);
		}
		60% {
			transform: translateX(5px);
		}
	}

	.solution-preview {
		padding: var(--space-6);
		background: var(--bg-primary);
		border: 1px solid rgba(255, 94, 126, 0.2);
		border-radius: var(--radius-lg);
		box-shadow: var(--shadow-lg);
	}

	.solution-header {
		font-size: var(--font-size-lg);
		font-weight: var(--font-weight-semibold);
		color: var(--text-primary);
		text-align: center;
		margin-bottom: var(--space-4);
	}

	.metrics-card {
		display: grid;
		grid-template-columns: 1fr 1fr;
		gap: var(--space-4);
	}

	.metric {
		padding: var(--space-3);
		background: var(--bg-secondary);
		border-radius: var(--radius-md);
		text-align: center;
		transition: all 0.3s ease;
		opacity: 0.7;
	}

	.metric.active {
		background: rgba(255, 94, 126, 0.1);
		border: 1px solid rgba(255, 94, 126, 0.3);
		opacity: 1;
		transform: scale(1.05);
	}

	.metric__value {
		font-size: var(--font-size-lg);
		font-weight: var(--font-weight-bold);
		color: rgb(255, 94, 126);
	}

	.metric__label {
		font-size: var(--font-size-xs);
		color: var(--text-secondary);
		margin-top: var(--space-1);
	}

	/* Horrors Section */
	.horrors {
		padding: var(--space-20) 0;
		background: var(--bg-secondary);
	}

	.horrors__header {
		text-align: center;
		margin-bottom: var(--space-16);
	}

	.horrors__title {
		font-size: var(--font-size-3xl);
		font-weight: var(--font-weight-semibold);
		color: var(--text-primary);
		margin-bottom: var(--space-4);
		letter-spacing: -0.02em;
	}

	.horrors__subtitle {
		font-size: var(--font-size-lg);
		color: var(--text-secondary);
	}

	.horrors__list {
		max-width: 1000px;
		margin: 0 auto;
		display: flex;
		flex-direction: column;
		gap: var(--space-12);
	}

	.horror-story {
		display: grid;
		grid-template-columns: 1fr 1fr;
		gap: var(--space-8);
		animation: slideIn 0.6s ease-out both;
	}

	@keyframes slideIn {
		from {
			opacity: 0;
			transform: translateY(30px);
		}
		to {
			opacity: 1;
			transform: translateY(0);
		}
	}

	.horror-story__scenario {
		padding: var(--space-6);
		background: var(--bg-primary);
		border: 1px solid var(--border-light);
		border-radius: var(--radius-lg);
		border-left: 4px solid #ef4444;
	}

	.horror-story__title {
		font-size: var(--font-size-xl);
		font-weight: var(--font-weight-semibold);
		color: var(--text-primary);
		margin: 0 0 var(--space-4) 0;
	}

	.horror-story__pain {
		display: flex;
		gap: var(--space-3);
		align-items: flex-start;
	}

	.horror-story__pain p {
		font-size: var(--font-size-base);
		color: var(--text-secondary);
		line-height: 1.6;
		margin: 0;
		font-style: italic;
	}

	.horror-story__solution {
		padding: var(--space-6);
		background: rgba(255, 94, 126, 0.05);
		border: 1px solid rgba(255, 94, 126, 0.2);
		border-radius: var(--radius-lg);
		border-left: 4px solid rgb(255, 94, 126);
		display: flex;
		align-items: center;
	}

	.horror-story__fix {
		display: flex;
		gap: var(--space-3);
		align-items: flex-start;
	}

	.horror-story__fix p {
		font-size: var(--font-size-base);
		color: var(--text-primary);
		line-height: 1.6;
		margin: 0;
	}

	/* Solutions Section */
	.solutions {
		padding: var(--space-20) 0;
	}

	.solutions__header {
		text-align: center;
		margin-bottom: var(--space-16);
	}

	.solutions__title {
		font-size: var(--font-size-3xl);
		font-weight: var(--font-weight-semibold);
		color: var(--text-primary);
		margin-bottom: var(--space-4);
		letter-spacing: -0.02em;
	}

	.solutions__subtitle {
		font-size: var(--font-size-lg);
		color: var(--text-secondary);
	}

	.solutions__grid {
		display: grid;
		grid-template-columns: repeat(2, 1fr);
		gap: var(--space-8);
		max-width: 1000px;
		margin: 0 auto;
	}

	.solution-card {
		display: flex;
		gap: var(--space-6);
		padding: var(--space-6);
		background: var(--bg-primary);
		border: 1px solid var(--border-light);
		border-radius: var(--radius-lg);
		transition: all 0.3s ease;
	}

	.solution-card:hover {
		box-shadow: var(--shadow-lg);
		transform: translateY(-4px);
		border-color: rgba(255, 94, 126, 0.3);
	}

	.solution-card__icon {
		flex-shrink: 0;
		width: 60px;
		height: 60px;
		background: rgba(255, 94, 126, 0.1);
		border-radius: var(--radius-lg);
		display: flex;
		align-items: center;
		justify-content: center;
	}

	.solution-card__title {
		font-size: var(--font-size-lg);
		font-weight: var(--font-weight-semibold);
		color: var(--text-primary);
		margin: 0 0 var(--space-3) 0;
	}

	.solution-card__description {
		font-size: var(--font-size-base);
		color: var(--text-secondary);
		line-height: 1.6;
		margin: 0 0 var(--space-4) 0;
	}

	.solution-card__benefit {
		display: flex;
		align-items: center;
		gap: var(--space-2);
		font-size: var(--font-size-sm);
		color: var(--text-primary);
		font-weight: var(--font-weight-medium);
	}

	/* Before/After Section */
	.before-after {
		padding: var(--space-20) 0;
		background: linear-gradient(135deg, rgba(239, 68, 68, 0.05) 0%, rgba(255, 94, 126, 0.05) 100%);
	}

	.before-after__grid {
		display: grid;
		grid-template-columns: 1fr 1fr;
		gap: var(--space-12);
		max-width: 1000px;
		margin: 0 auto;
	}

	.before-after__side {
		padding: var(--space-8);
		border-radius: var(--radius-lg);
	}

	.before-after__side.before {
		background: rgba(239, 68, 68, 0.05);
		border: 1px solid rgba(239, 68, 68, 0.2);
	}

	.before-after__side.after {
		background: rgba(255, 94, 126, 0.05);
		border: 1px solid rgba(255, 94, 126, 0.2);
	}

	.before-after__title {
		display: flex;
		align-items: center;
		gap: var(--space-3);
		font-size: var(--font-size-xl);
		font-weight: var(--font-weight-semibold);
		color: var(--text-primary);
		margin: 0 0 var(--space-6) 0;
		letter-spacing: -0.02em;
	}

	.before-after__list {
		list-style: none;
		padding: 0;
		margin: 0;
	}

	.before-after__list li {
		display: flex;
		align-items: flex-start;
		gap: var(--space-3);
		padding: var(--space-3) 0;
		font-size: var(--font-size-base);
		color: var(--text-primary);
		line-height: 1.5;
		border-bottom: 1px solid var(--border-light);
	}

	.before-after__list li:last-child {
		border-bottom: none;
	}

	.before li::before {
		content: '❌';
		flex-shrink: 0;
		margin-top: 2px;
	}

	.after li::before {
		content: '✅';
		flex-shrink: 0;
		margin-top: 2px;
	}

	/* CTA Section */
	.cta {
		padding: var(--space-20) 0;
		text-align: center;
		background: linear-gradient(180deg, transparent 0%, var(--bg-secondary) 100%);
	}

	.cta__content {
		max-width: 700px;
		margin: 0 auto;
	}

	.cta__title {
		font-size: var(--font-size-3xl);
		font-weight: var(--font-weight-semibold);
		color: var(--text-primary);
		margin-bottom: var(--space-4);
		letter-spacing: -0.02em;
	}

	.cta__subtitle {
		font-size: var(--font-size-lg);
		color: var(--text-secondary);
		margin-bottom: var(--space-8);
		line-height: 1.6;
	}

	.cta__actions {
		display: flex;
		gap: var(--space-4);
		justify-content: center;
		flex-wrap: wrap;
	}

	/* Responsive */
	@media (max-width: 768px) {
		.hero__grid {
			grid-template-columns: 1fr;
			gap: var(--space-8);
		}

		.hero__title {
			font-size: 2rem;
		}

		.spreadsheet-nightmare {
			order: -1;
		}

		.horror-story {
			grid-template-columns: 1fr;
		}

		.solutions__grid {
			grid-template-columns: 1fr;
		}

		.solution-card {
			flex-direction: column;
			text-align: center;
		}

		.before-after__grid {
			grid-template-columns: 1fr;
			gap: var(--space-6);
		}
	}
</style>
